/**
 * The coverage rule on screen: a figure or an absence ("0", "none", "$0.0") may only be
 * shown for a source that is CONNECTED AND SYNCED. The API says what it could see once
 * (`coverage`, packages/shared SourceCoverage); a source it could not see gets this
 * not-connected state instead of a zero. A visible source with a genuine zero shows zero.
 */
import type { SourceCoverage } from '@waypoint/shared';

// 'contact' is any channel a touch can come from (mailbox, calendar, recorded calls),
// which is what the per-client last-touch and next-meeting facts are drawn from.
export type Need = 'crm' | 'money' | 'mailbox' | 'contact';

/** True only when the response affirmatively says the source is visible (no field = not visible). */
export const sees = (c: SourceCoverage | null | undefined, need: Need): boolean => c?.[need] === true;

export const NOT_CONNECTED: Record<Need, { action: string; cant: string }> = {
  crm: { action: 'Connect a CRM', cant: "Waypoint can't see your pipeline yet" },
  money: { action: 'Connect QuickBooks or Stripe', cant: "Waypoint can't see your revenue yet" },
  mailbox: { action: 'Connect email', cant: "Waypoint can't see which clients have gone quiet yet" },
  contact: { action: 'Connect email or calendar', cant: "Waypoint can't see your contact history yet" },
};

export const INTEGRATIONS_PATH = '/settings?tab=integrations';

/** "Connect a CRM — Waypoint can't see your pipeline yet" */
export const notConnectedLine = (need: Need): string => `${NOT_CONNECTED[need].action} — ${NOT_CONNECTED[need].cant}`;

/**
 * What the Clients "Needs Attention" card may say when it flags no one: only what was
 * actually checked. Names the unchecked sources instead of claiming a clean bill.
 */
export function nothingFlaggedLine(c: SourceCoverage | null | undefined): string {
  const checked = [sees(c, 'mailbox') && 'two-way contact', sees(c, 'money') && 'invoices'].filter(Boolean) as string[];
  const unseen = [!sees(c, 'mailbox') && 'email', !sees(c, 'money') && 'billing'].filter(Boolean) as string[];
  if (checked.length === 0) return `Nothing to show: connect ${unseen.join(' and ')} and Waypoint can flag clients who have gone quiet or are overdue.`;
  const rule = [sees(c, 'mailbox') && 'gone 45 days without a two-way touch', sees(c, 'money') && 'an invoice 30 or more days overdue'].filter(Boolean).join(' or has ');
  return `Nothing flagged on ${checked.join(' and ')}: no client has ${rule}.${unseen.length ? ` Waypoint can't see ${unseen.join(' or ')} yet.` : ''}`;
}

const KPI_NEEDS: Record<string, Need[]> = {
  revenue_collected: ['money'], projected_revenue: ['money'],
  pipeline_value: ['crm'], total_deal_value: ['crm'], deals_at_risk: ['crm'], evidence_forecast: ['crm'],
  weekly_close_rate: ['crm', 'money'],
  // A zero count needs every source that feeds it (the API's followUps / escalations claims).
  followups_due: ['mailbox', 'crm', 'money'],
  escalations: ['mailbox', 'money'],
};

/** The first source a KPI needs that Waypoint cannot see, or null (visible, or coverage not loaded yet). */
export function kpiGap(kpiId: string, c: SourceCoverage | null | undefined): Need | null {
  if (!c) return null;
  return (KPI_NEEDS[kpiId] ?? []).find((n) => !sees(c, n)) ?? null;
}
